import React, { useState, useEffect, useRef } from 'react'
import QRCode from 'qrcode'
import './QRCodeGenerator.css'

const QRCodeGenerator = () => {
  const [text, setText] = useState('')
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState('')
  const [isDarkMode, setIsDarkMode] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [qrHistory, setQrHistory] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState('')
  const [settings, setSettings] = useState({
    size: 256,
    errorCorrectionLevel: 'M',
    margin: 4,
    color: {
      dark: '#000000',
      light: '#FFFFFF'
    }
  })
  const [showSettings, setShowSettings] = useState(false)
  const canvasRef = useRef(null)

  // QR Code Templates
  const templates = [
    { id: 'url', name: 'Website URL', placeholder: 'https://example.com', icon: '🌐' },
    { id: 'email', name: 'Email', placeholder: 'mailto:contact@example.com', icon: '📧' },
    { id: 'phone', name: 'Phone', placeholder: 'tel:+1234567890', icon: '📞' },
    { id: 'vcard', name: 'Contact Card', placeholder: 'BEGIN:VCARD\nVERSION:3.0\nFN:John Doe\nTEL:+1234567890\nEMAIL:john@example.com\nEND:VCARD', icon: '👤' },
    { id: 'wifi', name: 'WiFi Network', placeholder: 'WIFI:S:NetworkName;T:WPA;P:Password;;', icon: '📶' },
    { id: 'custom', name: 'Custom Text', placeholder: 'Enter your custom message here...', icon: '✏️' }
  ]

  // Color Themes
  const colorThemes = [
    { name: 'Classic', dark: '#000000', light: '#FFFFFF' },
    { name: 'Ocean', dark: '#1a365d', light: '#e6fffa' },
    { name: 'Forest', dark: '#22543d', light: '#f0fff4' },
    { name: 'Sunset', dark: '#744210', light: '#fffaf0' },
    { name: 'Midnight', dark: '#2d3748', light: '#f7fafc' },
    { name: 'Crimson', dark: '#742a2a', light: '#fff5f5' }
  ]

  const generateQRCode = async () => {
    if (!text.trim()) {
      setError('Please enter some text to generate a QR code')
      return
    }

    setIsGenerating(true)
    setError('')

    try {
      const options = {
        width: settings.size,
        margin: settings.margin,
        color: {
          dark: settings.color.dark,
          light: settings.color.light
        },
        errorCorrectionLevel: settings.errorCorrectionLevel
      }

      const dataUrl = await QRCode.toDataURL(text, options)
      setQrCodeDataUrl(dataUrl)
      
      // Add to history
      const newQR = {
        id: Date.now(),
        text: text,
        dataUrl: dataUrl,
        timestamp: new Date().toISOString()
      }
      setQrHistory(prev => [newQR, ...prev.slice(0, 4)]) // Keep last 5
      
      // Show success message
      setShowSuccess(true)
      setTimeout(() => setShowSuccess(false), 3000)
    } catch (err) {
      setError('Failed to generate QR code. Please try again.')
      console.error('QR Code generation error:', err)
    } finally {
      setIsGenerating(false)
    }
  }

  const downloadQRCode = async (format = 'png') => {
    if (!qrCodeDataUrl) return

    try {
      if (format === 'svg') {
        const svgDataUrl = await QRCode.toString(text, {
          width: settings.size,
          margin: settings.margin,
          color: {
            dark: settings.color.dark,
            light: settings.color.light
          },
          errorCorrectionLevel: settings.errorCorrectionLevel,
          type: 'svg'
        })

        const blob = new Blob([svgDataUrl], { type: 'image/svg+xml' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `qr-code-${Date.now()}.svg`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } else {
        const link = document.createElement('a')
        link.href = qrCodeDataUrl
        link.download = `qr-code-${Date.now()}.png`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    } catch (err) {
      setError('Failed to download QR code. Please try again.')
      console.error('Download error:', err)
    }
  }

  const copyToClipboard = async () => {
    if (!qrCodeDataUrl) return
    
    try {
      const response = await fetch(qrCodeDataUrl)
      const blob = await response.blob()
      await navigator.clipboard.write([
        new ClipboardItem({
          [blob.type]: blob
        })
      ])
      
      setShowSuccess(true)
      setTimeout(() => setShowSuccess(false), 2000)
    } catch (err) {
      setError('Failed to copy to clipboard. Please try again.')
    }
  }

  const shareQRCode = async () => {
    if (!qrCodeDataUrl || !navigator.share) return
    
    try {
      const response = await fetch(qrCodeDataUrl)
      const blob = await response.blob()
      const file = new File([blob], 'qr-code.png', { type: 'image/png' })
      
      await navigator.share({
        title: 'QR Code',
        text: `QR Code for: ${text}`,
        files: [file]
      })
    } catch (err) {
      setError('Failed to share QR code. Please try again.')
    }
  }

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template.id)
    setText(template.placeholder)
    setError('')
  }

  const handleThemeSelect = (theme) => {
    setSettings(prev => ({
      ...prev,
      color: {
        dark: theme.dark,
        light: theme.light
      }
    }))
  }

  const handleTextChange = (e) => {
    setText(e.target.value)
    if (error) setError('')
  }

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const handleColorChange = (type, value) => {
    setSettings(prev => ({
      ...prev,
      color: {
        ...prev.color,
        [type]: value
      }
    }))
  }

  const getCharacterCount = () => {
    return text.length
  }

  const getEncodingType = () => {
    if (text.startsWith('http://') || text.startsWith('https://')) return 'URL'
    if (text.startsWith('mailto:')) return 'Email'
    if (text.startsWith('tel:')) return 'Phone'
    if (text.startsWith('BEGIN:VCARD')) return 'vCard'
    if (text.startsWith('WIFI:')) return 'WiFi'
    return 'Text'
  }

  useEffect(() => {
    if (text.trim()) {
      const debounceTimer = setTimeout(() => {
        generateQRCode()
      }, 500)

      return () => clearTimeout(debounceTimer)
    }
  }, [text, settings])

  useEffect(() => {
    document.body.classList.toggle('dark-mode', isDarkMode)
  }, [isDarkMode])

  return (
    <div className={`qr-generator ${isDarkMode ? 'dark-mode' : ''}`} id="generator">
      <div className="generator-container">
        <div className="generator-header animate-fade-in-up">
          <div className="header-top">
            <h2 className="generator-title">Create Your QR Code</h2>
            <button
              className="theme-toggle"
              onClick={() => setIsDarkMode(!isDarkMode)}
              aria-label={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}
            >
              {isDarkMode ? '☀️' : '🌙'}
            </button>
          </div>
          <p className="generator-subtitle">
            Transform any text into a beautiful, scannable QR code. 
            Perfect for URLs, contact information, or custom messages.
          </p>
        </div>

        <div className="generator-content">
          <div className="input-section animate-fade-in-up">
            {/* Templates Section */}
            <div className="templates-section">
              <h3 className="section-title">Quick Templates</h3>
              <div className="templates-grid">
                {templates.map(template => (
                  <button
                    key={template.id}
                    className={`template-btn ${selectedTemplate === template.id ? 'active' : ''}`}
                    onClick={() => handleTemplateSelect(template)}
                    aria-label={`Use ${template.name} template`}
                  >
                    <span className="template-icon">{template.icon}</span>
                    <span className="template-name">{template.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="qr-text" className="input-label">
                Enter your text or URL
              </label>
              <textarea
                id="qr-text"
                className="text-input"
                value={text}
                onChange={handleTextChange}
                placeholder="Enter any text, URL, or message to encode..."
                rows={3}
                aria-describedby="input-help"
              />
              <div className="input-analytics">
                <span className="char-count">{getCharacterCount()} characters</span>
                <span className="encoding-type">{getEncodingType()} encoding</span>
              </div>
              <p id="input-help" className="input-help">
                Examples: https://example.com, vCard contact info, or any custom message
              </p>
            </div>

            <button
              className="settings-toggle"
              onClick={() => setShowSettings(!showSettings)}
              aria-expanded={showSettings}
              aria-controls="settings-panel"
            >
              <span className="settings-icon">⚙️</span>
              Customize Options
            </button>

            {showSettings && (
              <div id="settings-panel" className="settings-panel animate-fade-in">
                <h3 className="settings-title">QR Code Settings</h3>
                
                {/* Color Themes */}
                <div className="themes-section">
                  <h4 className="subsection-title">Color Themes</h4>
                  <div className="themes-grid">
                    {colorThemes.map(theme => (
                      <button
                        key={theme.name}
                        className="theme-btn"
                        onClick={() => handleThemeSelect(theme)}
                        style={{
                          '--theme-dark': theme.dark,
                          '--theme-light': theme.light
                        }}
                        aria-label={`Apply ${theme.name} theme`}
                      >
                        <div className="theme-preview">
                          <div className="theme-dark" style={{ backgroundColor: theme.dark }}></div>
                          <div className="theme-light" style={{ backgroundColor: theme.light }}></div>
                        </div>
                        <span className="theme-name">{theme.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="settings-grid">
                  <div className="setting-group">
                    <label htmlFor="qr-size" className="setting-label">
                      Size (pixels)
                    </label>
                    <select
                      id="qr-size"
                      value={settings.size}
                      onChange={(e) => handleSettingChange('size', parseInt(e.target.value))}
                      className="setting-select"
                    >
                      <option value={128}>128px</option>
                      <option value={256}>256px</option>
                      <option value={512}>512px</option>
                      <option value={1024}>1024px</option>
                    </select>
                  </div>

                  <div className="setting-group">
                    <label htmlFor="qr-error-level" className="setting-label">
                      Error Correction
                    </label>
                    <select
                      id="qr-error-level"
                      value={settings.errorCorrectionLevel}
                      onChange={(e) => handleSettingChange('errorCorrectionLevel', e.target.value)}
                      className="setting-select"
                    >
                      <option value="L">Low (7%)</option>
                      <option value="M">Medium (15%)</option>
                      <option value="Q">Quartile (25%)</option>
                      <option value="H">High (30%)</option>
                    </select>
                  </div>

                  <div className="setting-group">
                    <label htmlFor="qr-margin" className="setting-label">
                      Margin
                    </label>
                    <select
                      id="qr-margin"
                      value={settings.margin}
                      onChange={(e) => handleSettingChange('margin', parseInt(e.target.value))}
                      className="setting-select"
                    >
                      <option value={0}>None</option>
                      <option value={1}>Small</option>
                      <option value={4}>Medium</option>
                      <option value={8}>Large</option>
                    </select>
                  </div>

                  <div className="setting-group">
                    <label htmlFor="qr-dark-color" className="setting-label">
                      Dark Color
                    </label>
                    <input
                      id="qr-dark-color"
                      type="color"
                      value={settings.color.dark}
                      onChange={(e) => handleColorChange('dark', e.target.value)}
                      className="color-input"
                      aria-label="Choose dark color for QR code"
                    />
                  </div>

                  <div className="setting-group">
                    <label htmlFor="qr-light-color" className="setting-label">
                      Light Color
                    </label>
                    <input
                      id="qr-light-color"
                      type="color"
                      value={settings.color.light}
                      onChange={(e) => handleColorChange('light', e.target.value)}
                      className="color-input"
                      aria-label="Choose light color for QR code"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="preview-section animate-scale-in">
            <div className="qr-preview-container">
              {isGenerating ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p className="loading-text">Generating QR Code...</p>
                </div>
              ) : qrCodeDataUrl ? (
                <div className="qr-preview">
                  <img
                    src={qrCodeDataUrl}
                    alt="Generated QR Code"
                    className="qr-image"
                    ref={canvasRef}
                  />
                  <div className="qr-actions">
                    <button
                      onClick={() => downloadQRCode('png')}
                      className="download-btn primary"
                      aria-label="Download QR code as PNG"
                    >
                      <span className="btn-icon">📥</span>
                      Download PNG
                    </button>
                    <button
                      onClick={() => downloadQRCode('svg')}
                      className="download-btn secondary"
                      aria-label="Download QR code as SVG"
                    >
                      <span className="btn-icon">📄</span>
                      Download SVG
                    </button>
                    <button
                      onClick={copyToClipboard}
                      className="action-btn"
                      aria-label="Copy QR code to clipboard"
                    >
                      <span className="btn-icon">📋</span>
                      Copy
                    </button>
                    {navigator.share && (
                      <button
                        onClick={shareQRCode}
                        className="action-btn"
                        aria-label="Share QR code"
                      >
                        <span className="btn-icon">📤</span>
                        Share
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="empty-state">
                  <div className="empty-icon">📱</div>
                  <p className="empty-text">
                    Enter text above to generate your QR code
                  </p>
                </div>
              )}
            </div>

            {error && (
              <div className="error-message" role="alert">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}

            {showSuccess && (
              <div className="success-message" role="status">
                <span className="success-icon">✅</span>
                QR Code generated successfully!
              </div>
            )}
          </div>

          {/* QR History */}
          {qrHistory.length > 0 && (
            <div className="history-section animate-fade-in-up">
              <h3 className="section-title">Recent QR Codes</h3>
              <div className="history-grid">
                {qrHistory.map(qr => (
                  <div key={qr.id} className="history-item">
                    <img
                      src={qr.dataUrl}
                      alt="Recent QR Code"
                      className="history-qr"
                    />
                    <div className="history-info">
                      <p className="history-text">{qr.text.substring(0, 30)}...</p>
                      <span className="history-time">
                        {new Date(qr.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default QRCodeGenerator 